import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useFetch } from "../hooks/useFetch";
import { apiEndpoints, API_BASE_URL } from "../utils/api";

export const Carousel = () => {
  const {
    data: carousels,
    loading,
    error,
  } = useFetch(apiEndpoints.carousels.active());
  const [currentSlide, setCurrentSlide] = useState(0);

  // Slides por defecto si no hay datos de la API
  const defaultSlides = [
    {
      id: 1,
      title: "Red de Comunicaciones de Arauca",
      description:
        "Fortaleciendo el periodismo local y conectando a nuestra comunidad a través de la información veraz y oportuna.",
      image:
        "https://img.freepik.com/premium-psd/communication-concept-net-dark-background-with-glowing-dots_501590-2114.jpg",
      button_text: "Conoce Más",
      link: { type: "internal", url: "/nosotros" },
    },
    {
      id: 2,
      title: "Medios Asociados en Todo el Departamento",
      description:
        "Más de 25 medios de comunicación trabajando juntos por un periodismo de calidad en Arauca.",
      image:
        "https://img.freepik.com/free-vector/bright-green-technological-background-with-perspective_1035-4678.jpg",
      button_text: "Ver Miembros",
      link: { type: "internal", url: "/miembros" },
    },
    {
      id: 3,
      title: "Noticias y Actualidad Regional",
      description:
        "Mantente informado con las últimas noticias del departamento y la región.",
      image:
        "https://img.freepik.com/free-vector/gradient-network-connection-background_23-2148885875.jpg",
      button_text: "Leer Noticias",
      link: { type: "internal", url: "/noticias" },
    },
  ];

  // Usar datos de API si están disponibles, si no usar los por defecto
  const slides = carousels && carousels.length > 0 ? carousels : defaultSlides;

  // Auto-advance del carrusel
  useEffect(() => {
    if (slides.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000); // Cambiar cada 5 segundos

    return () => clearInterval(timer);
  }, [slides.length]);

  // Navegación manual
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div
      style={{
        position: "relative",
        height: "500px",
        overflow: "hidden",
        backgroundColor: "#1f2937",
      }}
    >
      {/* Loading State */}
      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#1f2937",
            color: "white",
          }}
        >
          <div className="spinner" style={{ marginRight: "12px" }}></div>
          <span>Cargando carrusel...</span>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div
          style={{
            position: "absolute",
            top: "16px",
            left: "16px",
            backgroundColor: "rgba(220, 38, 38, 0.9)",
            color: "white",
            padding: "8px 12px",
            borderRadius: "4px",
            fontSize: "12px",
            zIndex: 10,
          }}
        >
          API Error: {error}
        </div>
      )}

      {/* Slides */}
      {!loading &&
        slides.map((slide, index) => (
          <div
            key={slide.id}
            style={{
              position: "absolute",
              inset: 0,
              opacity: index === currentSlide ? 1 : 0,
              transition: "opacity 0.6s ease-in-out",
              zIndex: index === currentSlide ? 2 : 1,
            }}
          >
            {/* Background Image */}
            <img
              src={carousels ? `${API_BASE_URL}${slide.image}` : slide.image}
              alt={slide.title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={(e) => {
                console.log(
                  "❌ Error cargando imagen del carrusel:",
                  e.target.src
                );
                e.target.style.display = "none";
                e.target.nextElementSibling.style.display = "flex";
              }}
            />

            {/* Fallback si falla la imagen */}
            <div
              style={{
                display: "none",
                width: "100%",
                height: "100%",
                backgroundColor: "#4b5563",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                flexDirection: "column",
              }}
            >
              <i
                className="fas fa-image"
                style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.5 }}
              ></i>
              <span style={{ opacity: 0.7 }}>Imagen no disponible</span>
            </div>

            {/* Overlay */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(to right, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.3) 70%, transparent 100%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <div className="container" style={{ color: "white", zIndex: 3 }}>
                <div style={{ maxWidth: "600px" }}>
                  <h2
                    style={{
                      fontSize: window.innerWidth > 768 ? "48px" : "32px",
                      fontWeight: "bold",
                      marginBottom: "16px",
                      lineHeight: "1.2",
                      textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                    }}
                  >
                    {slide.title}
                  </h2>

                  {slide.description && (
                    <p
                      style={{
                        fontSize: window.innerWidth > 768 ? "18px" : "16px",
                        marginBottom: "32px",
                        lineHeight: "1.5",
                        textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)",
                        opacity: 0.95,
                      }}
                    >
                      {slide.description}
                    </p>
                  )}

                  {slide.button_text && slide.link && (
                    <Link
                      to={
                        slide.link.type === "post"
                          ? `/post/${slide.link.slug}`
                          : slide.link.url
                      }
                      style={{
                        backgroundColor: "#16a34a",
                        color: "white",
                        padding: "12px 24px",
                        borderRadius: "8px",
                        textDecoration: "none",
                        fontWeight: "500",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        boxShadow: "0 4px 12px rgba(22, 163, 74, 0.3)",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = "#15803d";
                        e.target.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = "#16a34a";
                        e.target.style.transform = "translateY(0)";
                      }}
                    >
                      {slide.button_text}
                      <i
                        className="fas fa-arrow-right"
                        style={{ fontSize: "14px" }}
                      ></i>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

      {/* Navigation Arrows - Solo mostrar si hay más de 1 slide */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            style={{
              position: "absolute",
              left: "20px",
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              border: "none",
              color: "white",
              padding: "16px",
              borderRadius: "50%",
              cursor: "pointer",
              fontSize: "18px",
              zIndex: 10,
              transition: "all 0.3s ease",
              backdropFilter: "blur(10px)",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "rgba(255, 255, 255, 0.3)";
              e.target.style.transform = "translateY(-50%) scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
              e.target.style.transform = "translateY(-50%) scale(1)";
            }}
          >
            <i className="fas fa-chevron-left"></i>
          </button>

          <button
            onClick={nextSlide}
            style={{
              position: "absolute",
              right: "20px",
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              border: "none",
              color: "white",
              padding: "16px",
              borderRadius: "50%",
              cursor: "pointer",
              fontSize: "18px",
              zIndex: 10,
              transition: "all 0.3s ease",
              backdropFilter: "blur(10px)",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "rgba(255, 255, 255, 0.3)";
              e.target.style.transform = "translateY(-50%) scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)";
              e.target.style.transform = "translateY(-50%) scale(1)";
            }}
          >
            <i className="fas fa-chevron-right"></i>
          </button>
        </>
      )}

      {/* Indicators - Solo mostrar si hay más de 1 slide */}
      {slides.length > 1 && (
        <div
          style={{
            position: "absolute",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: "12px",
            zIndex: 10,
          }}
        >
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                border: "2px solid white",
                backgroundColor:
                  index === currentSlide ? "white" : "transparent",
                cursor: "pointer",
                transition: "all 0.3s ease",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
              }}
              onMouseEnter={(e) => {
                if (index !== currentSlide) {
                  e.target.style.backgroundColor = "rgba(255, 255, 255, 0.7)";
                }
              }}
              onMouseLeave={(e) => {
                if (index !== currentSlide) {
                  e.target.style.backgroundColor = "transparent";
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Debug Info (solo en desarrollo) */}
      {/*
      {process.env.NODE_ENV === "development" && (
        <div
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            color: "white",
            padding: "8px 12px",
            borderRadius: "4px",
            fontSize: "12px",
            zIndex: 15,
          }}
        >
          API: {carousels ? "✅" : "❌"} | Slides: {slides.length} | Current:{" "}
          {currentSlide + 1}
        </div> 
      )}*/}
    </div>
  );
};
